import { Injectable } from "@angular/core";
import { ActivatedRoute, Data } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ModalController, NavParams } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subject, forkJoin, of, throwError } from "rxjs";
import { switchMap, take, takeUntil } from "rxjs/operators";
import { AuthService } from "@core/auth.service";
import { CommonVariable } from "src/app/app.constants";
import { CashPurchaseService } from "src/app/services/cash-purchase.service";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { PaymentDataService } from "src/app/services/payment-data.service";
import { PaymentService } from "src/app/services/payment.service";
import { StoreRepositoryService } from "../store-repository.service";
import { CardOfferService } from "./card-offer.service";
import { StorePaymentProcessorService } from "./checkout-payment.service";
import { CheckoutVoucherService } from "./checkout-voucher.service";

export interface StorePaymentInitConfig {
  authService: AuthService;
  cashPurchaseService: CashPurchaseService;
  gtmSegmentService: GTMSegmentService;
  cardOfferService: CardOfferService;
  paymentProcessorService: StorePaymentProcessorService;
  formBuilder: FormBuilder;
  route: ActivatedRoute;
  destroy$: Subject<void>;
  preferredCurrency: string;
  onSetSlideToBuyStatus: (enabled: boolean) => void;
  onSetPromotions: (promotions: Data[]) => void;
  onSetOfferTimer: (minutes: number, seconds: number) => void;
  onStartCardCountdowntimer: () => void;
  onLoadCards: () => void;
  onGetUpsellData: (upsellId: string) => void;
}

export interface StorePaymentConstructorConfig {
  state: any;
  authService: AuthService;
  translate: TranslateService;
  storeService: StoreRepositoryService;
  paymentService: PaymentService;
  paymentDataService: PaymentDataService;
  checkoutVoucherService: CheckoutVoucherService;
  gtmSegmentService: GTMSegmentService;
  navParams: NavParams;
  modalCtrl: ModalController;
  route: ActivatedRoute;
}

export interface StorePaymentInitResult {
  userId: string;
  journeyId: number;
  firstFormGroup: FormGroup;
  prosub_id: string;
  tp1: string;
  tp2: string;
  tp3: string;
  t_id: string;
  other: string;
  upsell_ID: string;
  paymentOfferID: string;
}

@Injectable()
export class StorePaymentInitService {
  setupConstructor(config: StorePaymentConstructorConfig): void {
    const s = config.state;

    s.preferredCurrency = CommonVariable.preferredCurrency;
    s.currencySymbol = CommonVariable.defaultCurrencySymbol;
    s.upsellList = [];
    s.checkedValues = [];
    s.addonAmount = [];
    s.paymentOfferID = "";
    s.updatedPrizeValue = 0;
    s.checkedValuesDefaultCurrency = [];
    s.upsellSummaryArray = [];

    config.authService.selectedlanguage$
      .pipe(takeUntil(s.destroy$))
      .subscribe(
        (l) => {
          s.selectedLanguage = l;
          config.translate.use(l ? l : "en");
        },
        (err) => err
      );

    config.authService.getConstantsMsg$
      ?.pipe(takeUntil(s.destroy$))
      .subscribe((msg) => {
        s.translatedToastMsg = msg?.static_messages_pwa;
      });

    config.authService.userDetails$
      ?.pipe(
        takeUntil(s.destroy$),
        switchMap((user) => {
          s.userData = user;
          s.preferredCurrencySignal.set(user?.preferred_currency ?? CommonVariable.preferredCurrency);
          s.preferredCurrency = s.preferredCurrencySignal();

          return config.authService.getCurrencyList$?.pipe(
            takeUntil(s.destroy$),
            take(1)
          ) ?? of(null);
        })
      )
      .subscribe({
        next: (data: Data) => {
          if (!data) return;
          const CurrencyDetails = data?.details?.find(
            (x) => x?.currency_code === s.preferredCurrencySignal()
          );
          s.currencySymbolSignal.set(CurrencyDetails?.currency_symbol ?? CommonVariable.defaultCurrencySymbol);
          s.currencySymbol = s.currencySymbolSignal();
        },
        error: (err) => {
          return config.authService.errorHandlerLogs("Error fetching currency list", err);
        },
      });

    config.gtmSegmentService.GTMPopupInitialization("StorePayment");
    s.purchaseDetail = config.navParams.get("data");

    if (!s.purchaseDetail?.selectedDiscount) {
      config.storeService.storeOffers$
        .pipe(takeUntil(s.destroy$))
        .subscribe((data) => {
          const hasValidDiscount =
            data?.cash_offer_discount &&
            Array.isArray(data.cash_offer_discount) &&
            data.cash_offer_discount.length > 0 &&
            data.cash_offer_discount[0];

          s.purchaseDetail = {
            ...s.purchaseDetail,
            selectedDiscount: hasValidDiscount ? { ...data.cash_offer_discount[0] } : {},
          };
        });
    }

    config.checkoutVoucherService.initialize(s.purchaseDetail, s.preferredCurrencySignal());
    s.purchaseDetail["prize"] = Number(s.purchaseDetail?.prize);

    config.paymentService
      .getPTypeByPreferredCurrency(s.preferredCurrencySignal())
      .pipe(take(1))
      .subscribe({
        next: (currencyData) => {
          s.currencyBasedPTypeSignal.set(currencyData[0]?.id ? currencyData[0].id : "10");
          s.currencyBasedPType = s.currencyBasedPTypeSignal();
        },
        error: (err) => {
          throwError(err);
        },
      });

    if (!s.purchaseDetail || !s.purchaseDetail?.products) {
      config.modalCtrl.dismiss().catch((err) => err);
    }

    s.purchaseDetail.productRealName = [];
    if (s.purchaseDetail?.productid?.length) {
      const requests = s.purchaseDetail.productid.map((element) => config.paymentDataService?.getLotteryDetails(element));
      forkJoin(requests).subscribe({
        next: (values: Data[]) => {
          values.forEach((value, i) => {
            s.purchaseDetail.productRealName[i] = value?.real_name;
          });
        },
        error: (error) => error,
      });
    }

    if (s.purchaseDetail?.page === "syndicate") {
      s.totalLine = s.purchaseDetail?.lines
        ? s.purchaseDetail?.lines
        : s.purchaseDetail?.products?.length > 1
          ? s.purchaseDetail?.products.map((l) => l?.line_count * l?.draw_day_group?.length)?.reduce((a, b) => a + b)
          : s.purchaseDetail?.products?.reduce((prev, next) => prev.line_count + next.line_count).line_count;
      s.purchaseDetail["totalLine"] = s.totalLine;
    }

    const offer_p_type = s.purchaseDetail?.offers?.p_type;
    config.paymentService
      .getPTypeByOffer(offer_p_type)
      .pipe(take(1))
      .subscribe({
        next: (currency: Data) => {
          const offerCurrencyData = currency?.data() as { valid_currency: Array<string> };
          const offerValidCurrencyAry = offerCurrencyData?.valid_currency;
          if (Array.isArray(offerValidCurrencyAry) && offerValidCurrencyAry.includes(s.preferredCurrencySignal())) {
            s.pType = offer_p_type;
          } else {
            s.pType = s.currencyBasedPTypeSignal();
          }
          s.selectedCard = s.pType;
          s.selectedCardForAddCardCase = s.pType;
          s.purchaseDetail["p_type"] = s.selectedCard;
          config.paymentService.selected_p_type = s.selectedCard;
          s.selectedCard$.next(s.selectedCard);
        },
        error: () => {
          s.selectedCard$.next(s.selectedCard);
        },
      });

    config.storeService.closeModal$
      .pipe(takeUntil(s.destroy$))
      .subscribe(() => {
        s.openTickets();
      });

    config.storeService.customOfferCashBackText$
      .pipe(takeUntil(s.destroy$))
      .subscribe(
        (text: string) => {
          s.customOfferCashbackText = text;
        },
        (err) => err
      );

    s.customOfferAwardIdSubscription = config.storeService.customOfferAwardId$.subscribe(
      (awardId: number) => {
        s.customOfferAwardId = awardId;
      },
      (err) => err
    );

    const offer_checkout_description = s.purchaseDetail?.offers?.checkout_description;
    config.authService.currencyNodeDvData = s.purchaseDetail?.offers;
    if (offer_checkout_description) {
      config.authService.getDVFromString(offer_checkout_description).then((dvString: string) => {
        s.checkout_description = dvString;
      });
    }

    const qp = config.route.snapshot?.queryParams ?? {};
    const isSandBoxStatus = qp.pt === "sandbox" ? true : false;
    s.isSandbox.set(isSandBoxStatus);
  }

  async initialize(config: StorePaymentInitConfig): Promise<StorePaymentInitResult> {
    const userId = await this.getUserId(config.authService);
    await this.initializeOneClickConfig(config, userId);

    config.onStartCardCountdowntimer();
    const journeyId = parseInt(localStorage.getItem("journeyId") || "0", 10);
    config.onLoadCards();

    const firstFormGroup = config.formBuilder.group({
      firstCtrl: ["", Validators.required],
    });

    const queryResult = this.parseAndPersistQueryParams(config.route, config.gtmSegmentService);

    if (queryResult.upsell_ID && queryResult.paymentOfferID) {
      config.onGetUpsellData(queryResult.upsell_ID);
    }

    await config.cardOfferService.initialize(userId, config.preferredCurrency);
    this.bindCardOfferStreams(config);
    config.cardOfferService.startCountdown();

    return {
      userId,
      journeyId,
      firstFormGroup,
      ...queryResult,
    };
  }

  private async getUserId(authService: AuthService): Promise<string> {
    try {
      return await authService?.getUid("StorePaymentComponent");
    } catch {
      return "";
    }
  }

  private async initializeOneClickConfig(
    config: StorePaymentInitConfig,
    userId: string
  ): Promise<void> {
    const req = {
      parameter: "one_click_pay",
      condition: "one_click_pay_condition",
      feature: "one_click_pay",
      userId,
    };

    try {
      const res = await config.authService.getRemoteConfig(req);
      const remoteConfigData = res?.data;
      const showOneClick = remoteConfigData?.one_click_pay
        ? remoteConfigData?.one_click_pay
        : remoteConfigData;

      if (showOneClick) {
        config.cashPurchaseService.storedCards$
          .pipe(takeUntil(config.destroy$))
          .subscribe((cards) => {
            config.onSetSlideToBuyStatus(cards?.length > 0);
          });
      }
    } catch {
      return;
    }
  }

  private parseAndPersistQueryParams(
    route: ActivatedRoute,
    gtmSegmentService: GTMSegmentService
  ): Omit<StorePaymentInitResult, "userId" | "journeyId" | "firstFormGroup"> {
    const queryParams = { ...route.snapshot.queryParams };

    let prosub_id = "";
    if (queryParams?.Prosub_id) prosub_id = queryParams.Prosub_id;
    else if (queryParams?.prosub_id) prosub_id = queryParams.prosub_id;
    else if (queryParams?.Prosub_ID) prosub_id = queryParams.Prosub_ID;

    delete queryParams["Prosub_ID"];
    delete queryParams["Prosub_id"];
    delete queryParams["prosub_id"];

    gtmSegmentService.queryParamsToNavigate = queryParams;

    const tp1 = queryParams.TP1 ? queryParams.TP1 : queryParams.tp1 ? queryParams.tp1 : "";
    const upsell_ID = queryParams?.upsell_ID ? queryParams?.upsell_ID : "";
    const paymentOfferID = queryParams?.payment ? queryParams?.payment : "";
    const tp2 = queryParams.TP2 ? queryParams.TP2 : queryParams.tp2 ? queryParams.tp2 : "";
    const tp3 = queryParams[Object.keys(queryParams)?.find((key) => key?.toLowerCase() === "tp3")] ?? "";
    const t_id = queryParams[Object.keys(queryParams)?.find((key) => key?.toLowerCase() === "t_id")] ?? "";
    const other = queryParams.Other ? queryParams.Other : queryParams.other ? queryParams.other : "";

    if (prosub_id) localStorage.setItem("prosub_id", prosub_id);
    if (other) localStorage.setItem("Other", other);
    if (tp1) localStorage.setItem("TP1", tp1);
    if (tp2) localStorage.setItem("TP2", tp2);
    if (tp3) localStorage.setItem("TP3", tp3);
    if (t_id) localStorage.setItem("T_ID", t_id);
    if (upsell_ID) localStorage.setItem("upsellID", upsell_ID);
    if (paymentOfferID) localStorage.setItem("paymentOfferID", paymentOfferID);

    return {
      prosub_id,
      tp1,
      tp2,
      tp3,
      t_id,
      other,
      upsell_ID,
      paymentOfferID,
    };
  }

  private bindCardOfferStreams(config: StorePaymentInitConfig): void {
    config.cardOfferService.promotions$
      .pipe(takeUntil(config.destroy$))
      .subscribe((promotions) => {
        config.onSetPromotions(promotions || []);
      });

    config.cardOfferService.processingStates$
      .pipe(takeUntil(config.destroy$))
      .subscribe((states) => {
        config.paymentProcessorService.processingStates = states || [];
      });

    config.cardOfferService.timer$
      .pipe(takeUntil(config.destroy$))
      .subscribe((timer) => {
        config.onSetOfferTimer(timer?.minutes ?? 0, timer?.seconds ?? 0);
      });
  }
}
